/**
 * Industry design system — MUI theme for the logged-in area.
 *
 * Applied by DashboardLayout through a nested ThemeProvider so every
 * authenticated page (dialogs, menus and other portaled surfaces included —
 * the theme travels through React context, not the DOM) speaks the approved
 * design language: steel-blue accent on a light technical ground, Barlow
 * Condensed headings over Barlow body, square corners, hairline borders.
 *
 * Public pages (landing/login) keep the previous base theme on purpose.
 * Spacing and breakpoints stay at MUI defaults so responsive behavior is
 * untouched by the migration.
 */

import { createTheme } from '@mui/material/styles';

// Token mirror of styles/industry-tokens.css — kept as literals because MUI
// resolves palette derivations (hover ripples, alpha channels) at build time.
export const IND = {
  bg: '#f2f2f3',
  surface: '#e9e9ea',
  paper: '#ffffff',
  text: '#1d1f20',
  textSecondary: '#5d5d60',
  textMuted: '#7a7a7d',
  divider: 'rgba(29,31,32,0.16)',
  dividerSoft: 'rgba(29,31,32,0.08)',
  accent: '#5980a6',
  accent100: '#eef6ff',
  accent200: '#d6ebff',
  accent400: '#94bce3',
  accent600: '#597ea3',
  accent700: '#416180',
  accent800: '#2c455d',
  accent900: '#1d2d3d',
  navBg: '#0d1b2e',
  cyan: '#38d6ee',
  shadowSm: '0 1px 2px rgba(43,43,45,0.14)',
  shadowMd: '0 3px 10px rgba(43,43,45,0.16)',
  shadowLg: '0 12px 32px rgba(43,43,45,0.22)',
};

export const FONT_HEADING = "'Barlow Condensed', 'Barlow', system-ui, sans-serif";
export const FONT_BODY = "'Barlow', system-ui, sans-serif";

// The mockups' recurring label voice: condensed, tracked, uppercase.
const condensedLabel = (size = '11.5px', tracking = '0.1em') => ({
  fontFamily: FONT_HEADING,
  fontWeight: 600,
  fontSize: size,
  letterSpacing: tracking,
  textTransform: 'uppercase',
});

const industryTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: IND.accent,
      light: IND.accent400,
      dark: IND.accent700,
      contrastText: '#ffffff',
    },
    secondary: {
      main: IND.textMuted,
      light: '#98989b',
      dark: '#424244',
      contrastText: '#ffffff',
    },
    background: {
      default: IND.bg,
      paper: IND.paper,
    },
    text: {
      primary: IND.text,
      secondary: IND.textSecondary,
    },
    divider: IND.divider,
    // The approved canvas is a mono scheme: severity is expressed in ink and
    // steel (the eFRAT artboard renders NO-GO/ALTO in neutral-900 bordered
    // chips), never in red/green/amber. Mapping the semantic palette into the
    // same ramps keeps every `color="success|error|…"` usage on-system.
    error: { main: '#2b2b2d', light: '#424244', dark: '#1d1f20', contrastText: '#ffffff' },
    warning: { main: '#5d5d60', light: '#7a7a7d', dark: '#424244', contrastText: '#ffffff' },
    success: { main: IND.accent700, light: IND.accent400, dark: IND.accent800, contrastText: '#ffffff' },
    info: { main: IND.accent600, light: IND.accent400, dark: IND.accent700, contrastText: '#ffffff' },
  },

  typography: {
    fontFamily: FONT_BODY,
    fontSize: 14,
    h1: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '2rem', lineHeight: 1.12, letterSpacing: '-0.015em', color: IND.text },
    h2: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '1.625rem', lineHeight: 1.12, letterSpacing: '-0.015em', color: IND.text },
    h3: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.15, letterSpacing: '-0.01em', color: IND.text },
    h4: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '20px', lineHeight: 1.2, color: IND.text },
    h5: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '18px', lineHeight: 1.2, color: IND.text },
    h6: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '15px', lineHeight: 1.25, letterSpacing: '0.06em', textTransform: 'uppercase', color: IND.text },
    subtitle1: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '16px' },
    subtitle2: { fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '14px', letterSpacing: '0.06em', textTransform: 'uppercase' },
    body1: { fontSize: '16px', lineHeight: 1.55, color: IND.text },
    body2: { fontSize: '15px', lineHeight: 1.5, color: IND.textSecondary },
    button: { ...condensedLabel('14px', '0.06em') },
    caption: { fontSize: '13px', lineHeight: 1.45 },
    overline: { ...condensedLabel('12px', '0.16em'), lineHeight: 1.6 },
  },

  shape: {
    // Square-corner language: wireframe objects, never soft rounded blocks.
    borderRadius: 0,
  },

  shadows: [
    'none',
    IND.shadowSm, IND.shadowSm, IND.shadowSm,
    IND.shadowMd, IND.shadowMd, IND.shadowMd, IND.shadowMd, IND.shadowMd,
    IND.shadowLg, IND.shadowLg, IND.shadowLg, IND.shadowLg, IND.shadowLg,
    IND.shadowLg, IND.shadowLg, IND.shadowLg, IND.shadowLg, IND.shadowLg,
    IND.shadowLg, IND.shadowLg, IND.shadowLg, IND.shadowLg, IND.shadowLg,
    IND.shadowLg,
  ],

  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: `2px solid ${IND.accent}`,
            outlineOffset: 2,
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          minHeight: 34,
          padding: '5px 14px',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          border: `1px solid ${IND.accent}`,
          '&:hover': { backgroundColor: IND.accent600, boxShadow: 'none' },
          '&:active': { backgroundColor: IND.accent700 },
        },
        containedPrimary: {
          backgroundColor: IND.accent,
        },
        outlined: {
          borderColor: IND.divider,
          color: IND.text,
          '&:hover': { borderColor: 'rgba(29,31,32,0.45)', backgroundColor: 'rgba(29,31,32,0.05)' },
        },
        text: {
          color: IND.accent700,
          '&:hover': { backgroundColor: 'rgba(89,128,166,0.1)' },
        },
        sizeSmall: { minHeight: 30, padding: '3px 10px', fontSize: '13px' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
        },
        outlined: { borderColor: IND.divider },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${IND.divider}`,
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${IND.divider}`,
          boxShadow: IND.shadowLg,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: FONT_HEADING, fontWeight: 600, letterSpacing: '-0.01em' },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${IND.divider}`,
          boxShadow: IND.shadowMd,
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: 0 },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: 'transparent',
          '& fieldset': { borderColor: IND.divider },
          '&:hover fieldset': { borderColor: 'rgba(29,31,32,0.45)' },
          '&.Mui-focused fieldset': { borderColor: IND.accent, borderWidth: 1 },
        },
      },
    },

    MuiFilledInput: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${IND.dividerSoft}` },
        head: {
          ...condensedLabel('13px', '0.06em'),
          color: 'rgba(29,31,32,0.6)',
          borderBottom: `1px solid ${IND.divider}`,
          backgroundColor: 'transparent',
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: 'transparent',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(29,31,32,0.04)' },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontFamily: FONT_HEADING,
          fontWeight: 600,
          letterSpacing: '0.06em',
        },
        outlined: { borderColor: IND.divider },
        // Colored status chips become the DS tag voice: tinted steel fill for
        // positive/informational states, neutral ink for blocking ones —
        // matching the artboards' PENDENTE / LANÇADO / NO-GO chips.
        filledSuccess: { backgroundColor: IND.accent100, color: IND.accent800 },
        filledInfo: { backgroundColor: IND.accent100, color: IND.accent800 },
        filledPrimary: { backgroundColor: IND.accent100, color: IND.accent800 },
        filledError: { backgroundColor: '#e7e7ea', color: '#2b2b2d' },
        filledWarning: { backgroundColor: '#e7e7ea', color: '#424244' },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 0, border: `1px solid ${IND.divider}` },
        standardSuccess: { backgroundColor: IND.accent100, color: IND.accent800 },
        standardInfo: { backgroundColor: IND.accent100, color: IND.accent800 },
        standardError: { backgroundColor: '#f5f5f8', color: '#2b2b2d', borderColor: '#2b2b2d' },
        standardWarning: { backgroundColor: '#f5f5f8', color: '#424244' },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          ...condensedLabel('14px', '0.08em'),
          minHeight: 44,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        indicator: { height: 2, backgroundColor: IND.accent },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 0,
          fontFamily: FONT_BODY,
          fontSize: '13px',
          backgroundColor: IND.accent900,
        },
        arrow: { color: IND.accent900 },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          ...condensedLabel('13px', '0.06em'),
          '&.Mui-selected': {
            backgroundColor: IND.accent,
            color: '#ffffff',
            '&:hover': { backgroundColor: IND.accent600 },
          },
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${IND.divider}`,
          boxShadow: 'none',
          '&::before': { display: 'none' },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 0, backgroundColor: '#e7e7ea' },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: 0 },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_HEADING,
          // 16px is not a taste call: MUI shrinks the floating label with
          // scale(0.75), so this renders at 12px on a filled field — and the
          // notched outline sizes its gap at 0.75em of the INPUT font (body1,
          // 16px = 12px). Any other value here leaves the border gap wider
          // than the word sitting in it, which is what 10px was doing.
          fontSize: '16px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_HEADING,
          // 16px is not a taste call: MUI shrinks the floating label with
          // scale(0.75), so this renders at 12px on a filled field — and the
          // notched outline sizes its gap at 0.75em of the INPUT font (body1,
          // 16px = 12px). Any other value here leaves the border gap wider
          // than the word sitting in it, which is what 10px was doing.
          fontSize: '16px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: { fontFamily: FONT_BODY },
      },
    },
  },
});

export default industryTheme;
